import type {
  BlackboardEntry,
  Claim,
  Message,
  MissionTask,
  Runtime,
  Session,
  TraceEntry,
  WorkspaceFile,
} from '../types';
import type { Role, SessionInfo } from '../lib/api';
import type { SseStatus } from '../lib/sse';

export type LoadState = 'idle' | 'loading' | 'ready' | 'error';

export interface ToastItem {
  id: string;
  kind: 'info' | 'success' | 'warning' | 'danger';
  title: string;
  body?: string;
  actionLabel?: string;
  actionTarget?: string;
  sticky?: boolean;
}

export type ModalKind = 'new-request' | 'json' | 'confirm' | 'change-password';

export interface ModalState {
  kind: ModalKind;
  title: string;
  body?: string;
  okLabel?: string;
  danger?: boolean;
  payload?: Record<string, unknown>;
}

export interface AppState {
  authed: boolean;
  authChecked: boolean;
  user: string;
  // il profilo di chi è entrato: ruolo (admin, user, viewer), nome visualizzato e avatar
  role: Role;
  display: string;
  avatar: string | null;
  // §3.0 — finché è false la plancia mostra il primo avvio, non le Conversazioni
  ready: boolean;
  version: string;

  load: LoadState;
  loadError?: string;
  degraded: boolean;
  lastUpdate: string;

  sse: SseStatus;

  sessions: Session[];
  messages: Message[];
  runtime: Runtime;
  trace: TraceEntry[];
  blackboard: BlackboardEntry[];
  claims: Claim[];
  // per sessione: i lavori del piano e i file dello spazio di lavoro
  tasks: Record<string, MissionTask[]>;
  files: Record<string, WorkspaceFile[]>;

  toasts: ToastItem[];
  modal: ModalState | null;

  filters: {
    search: string;
    state: string;
    project: string;
    onlyWaiting: boolean;
    follow: boolean;
  };

  lane: string | null; // filtro per sessione sul Runtime
  drawer: {
    mode: 'anchored' | 'following';
    nodeId?: string;
    sessionId?: string;
    tab: 'reason' | 'cost' | 'board';
  } | null;
  level2: string | null; // nodeId aperto al livello 2
  playing: boolean;
  speed: 1 | 2 | 4;
  showRails: boolean;

  pendingOptimistic: Record<string, Session>; // per il rollback (§8)
}

export type Action =
  | { type: 'auth/info'; info: SessionInfo | null }
  | { type: 'auth/profile'; display?: string; avatar?: string | null }
  | { type: 'auth/logout' }
  | { type: 'load/start' }
  | { type: 'load/error'; error: string }
  | { type: 'load/done'; data: Partial<AppState> }
  | { type: 'sse/status'; status: SseStatus }
  | { type: 'sse/degraded'; degraded: boolean }
  | { type: 'toast/push'; toast: ToastItem }
  | { type: 'toast/close'; id: string }
  | { type: 'modal/open'; modal: ModalState }
  | { type: 'modal/close' }
  | { type: 'filters/set'; patch: Partial<AppState['filters']> }
  | { type: 'lane/set'; lane: string | null }
  | { type: 'drawer/open'; drawer: NonNullable<AppState['drawer']> }
  | { type: 'drawer/tab'; tab: 'reason' | 'cost' | 'board' }
  | { type: 'drawer/close' }
  | { type: 'level2/set'; nodeId: string | null }
  | { type: 'canvas/playing'; playing: boolean }
  | { type: 'canvas/speed'; speed: 1 | 2 | 4 }
  | { type: 'canvas/rails'; show: boolean }
  | { type: 'session/patch'; id: string; patch: Partial<Session> }
  | { type: 'session/add'; session: Session }
  | { type: 'session/optimistic'; id: string }
  | { type: 'session/rollback'; id: string }
  | { type: 'session/settle'; id: string }
  | { type: 'message/add'; message: Message }
  // la copia ottimistica diventa il messaggio del server, o sparisce se la risposta non è passata
  | { type: 'message/settle'; tempId: string; id: string | null }
  | { type: 'trace/add'; entry: TraceEntry }
  | { type: 'blackboard/add'; entry: BlackboardEntry }
  | { type: 'claim/upsert'; claim: Claim }
  | { type: 'tasks/set'; sessionId: string; tasks: MissionTask[] }
  | { type: 'task/upsert'; task: MissionTask }
  | { type: 'files/set'; sessionId: string; files: WorkspaceFile[] }
  | { type: 'file/change'; sessionId: string; file: WorkspaceFile; removed: boolean }
  | {
      type: 'runtime/patch';
      patch: {
        nodesAdded?: Runtime['nodes'];
        nodesChanged?: Partial<Runtime['nodes'][number]>[];
        nodesRemoved?: string[];
        rework?: Runtime['rework'];
      };
    };

export const EMPTY_RUNTIME: Runtime = {
  lanes: [],
  nodes: [],
  rework: [],
  packets: [],
  updatedAt: '',
};

export const initialState: AppState = {
  authed: false,
  authChecked: false,
  user: '',
  role: 'viewer',
  display: '',
  avatar: null,
  ready: false,
  version: '',
  load: 'idle',
  degraded: false,
  lastUpdate: new Date().toISOString(),
  sse: 'connecting',
  sessions: [],
  messages: [],
  runtime: EMPTY_RUNTIME,
  trace: [],
  blackboard: [],
  claims: [],
  tasks: {},
  files: {},
  toasts: [],
  modal: null,
  filters: { search: '', state: '', project: '', onlyWaiting: false, follow: true },
  lane: null,
  drawer: null,
  level2: null,
  playing: true,
  speed: 1,
  showRails: true,
  pendingOptimistic: {},
};
